import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '@/App';
import PollenDotsMap from '@/components/PollenDotsMap';
import ShareMapMenu from '@/components/ShareMapMenu';
import PollenDotsContactFooter from '@/components/PollenDotsContactFooter';
import PollenDotsLegend from '@/components/PollenDotsLegend';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MapPin, Bed, Users, Loader2, CalendarDays, Gem, Sparkles, UserRound, Heart, HandHeart, Download, Link2 } from 'lucide-react';

/**
 * Public share page for a single customer's travel footprint with Pollen Dots.
 * Editorial layout: single brand logo on top, hero copy, map as hero, stat
 * ribbon with ALL 7 customer counters below, icon-only contact footer.
 */
const PublicJourneyShare = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/public/journey/${token}`);
        if (!res.ok) setErr(res.status === 404 ? 'This link is no longer active.' : 'Could not load.');
        else setData(await res.json());
      } catch (e) { setErr('Network error. Please try again.'); }
      finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-teal-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-amber-50 via-rose-50 to-teal-50">
        <BrandBar />
        <MapPin className="w-10 h-10 text-muted-foreground/40 my-4" />
        <p className="text-muted-foreground">{err || 'Journey not found.'}</p>
      </div>
    );
  }

  const placesAll = data.places || [];
  const cityPlaces = placesAll.filter(p => p.kind !== 'enroute_stop' && p.kind !== 'activity');
  const stopPlaces = placesAll.filter(p => p.kind === 'enroute_stop');
  const activityPins = placesAll.filter(p => p.kind === 'activity');
  const pins = [
    ...cityPlaces.map(p => ({ ...p, type: 'place'  })),
    ...stopPlaces.map(p => ({ ...p, type: 'stop'   })),  // teardrop pins, not dots
    ...activityPins.map(p => ({ ...p, type: 'activity' })),
    ...(data.hotels  || []).map(p => ({ ...p, type: 'hotel'  })),
    ...(data.guides  || []).map(p => ({ ...p, type: 'guide'  })),
    ...(data.drivers || []).map(p => ({ ...p, type: 'driver' })),
  ];

  const shareUrl = data.og_share_url || (typeof window !== 'undefined' ? window.location.href : '');
  // First-name picker: strip common salutations (Mr., Mrs., Ms., Dr., Miss,
  // Smt., Shri, Sh., Sir, Madam) so "Mr. Sandesh Gottikere Shivaraju"
  // renders as "Sandesh" — not "Mr." — in the hero heading.
  const SALUTATIONS = /^(mr|mrs|ms|dr|miss|smt|shri|sh|sir|madam|mister)\.?$/i;
  const nameTokens = (data.customer_name || 'Traveller').split(/\s+/).filter(Boolean);
  const firstName = (nameTokens.find(t => !SALUTATIONS.test(t)) || nameTokens[0] || 'Traveller');
  const filename = `${(data.customer_name || 'journey').toLowerCase().replace(/\s+/g, '-')}-pollen-dots`;
  const peopleCount = (data.guides?.length || 0) + (data.drivers?.length || 0);

  return (
    <div className="min-h-screen" style={{ background: '#FCF8F7', color: '#030922' }}>
      <div className="max-w-3xl mx-auto px-4 pt-6 pb-10 sm:pt-8 sm:pb-12">
        <BrandBar />

        {/* Hero — pollendots.com brand language: Fraunces serif, gold kicker */}
        <header className="text-center mt-8 mb-6">
          <p className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-3" style={{ color: '#B16E32' }}>
            Travel · Connect · Care
          </p>
          <h1 className="text-3xl sm:text-5xl leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif" }} data-testid="share-customer-name">
            <span className="italic" style={{ color: '#581C2B' }}>{firstName}</span>
            <span className="italic" style={{ color: '#030922' }}>&rsquo;s Dots of Connection</span>
          </h1>
          <p className="text-[10px] tracking-[0.26em] uppercase mt-3" style={{ color: '#787878' }}>
            Made with Pollen Dots
          </p>
          <p className="text-sm max-w-lg mx-auto mt-3 leading-relaxed" style={{ color: '#6b5566' }}>
            Every place you touched, every hotel that hosted you, every guide and driver who walked with you &mdash; gently marked as dots of connection.
          </p>
        </header>

        {/* MAP — always rendered. Side padding on mobile so users can scroll
            past it by swiping the edge margin instead of getting stuck on the
            map's touch handling. Interior of the map stays fully pannable. */}
        <div className="relative mx-6 sm:mx-0 rounded-2xl overflow-hidden bg-white"
          style={{ boxShadow: '0 14px 44px -18px rgba(88,28,43,0.35)', border: '3px solid #B16E32' }}>
          <PollenDotsMap ref={mapRef} pins={pins} aspect="aspect-[4/5] sm:aspect-[3/4] max-h-[55vh] sm:max-h-[640px]" zoom={5} homeBounds={[[4.5, 63], [37.5, 99]]} />
        </div>
        <PollenDotsLegend />

        {/* Share CTA — sits as its own clean card so it never crowds the
            map or the legend. Customer-voice copy invites them to pass
            this page on to their friends. */}
        <div className="mx-6 sm:mx-0 mt-5 flex flex-col items-center gap-2">
          <p className="text-[11px] uppercase tracking-[0.18em] text-amber-700/70 font-medium">
            Pass this on to a friend
          </p>
          <ShareMapMenu
            mapRef={mapRef}
            shareUrl={shareUrl}
            whatsappText={`Look at the places I travelled with Pollen Dots! 🌸 Every dot here is a story of where I went, who hosted me, and the small acts of care along the way.`}
            filename={filename}
            variant="default"
            size="default"
            label="Share my journey"
          />
        </div>

        {/* Stat ribbon — 7 counters. Mobile: 2-col grid (wider cards = no truncation). */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <StatCard Icon={Sparkles}     tint="orange" label="Tours"   value={data.tours_count || 0}          testid="counter-tours" />
          <StatCard Icon={CalendarDays} tint="violet" label="Days"    value={data.days_travelled || 0}       testid="counter-days" />
          <StatCard Icon={MapPin}       tint="teal"   label="Places"  value={data.places?.length || 0}       testid="counter-places" />
          <StatCard Icon={UserRound}    tint="rose"   label="People"  value={peopleCount}                    testid="counter-people" />
        </div>
        <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          <StatCard Icon={Bed}          tint="sky"    label="Hotels"         value={data.hotels?.length || 0}  testid="counter-hotels" />
          <StatCard Icon={Heart}        tint="pink"   label="Friends"        value={data.friends_count || 0}   testid="counter-friends" />
          {data.loyalty_visible && data.loyalty_credits != null && (
            <div className="col-span-2 sm:col-span-1">
              <StatCard Icon={Gem}      tint="amber"  label="Loyalty Points" value={data.loyalty_credits}      testid="counter-loyalty-redeem" href="https://www.pollendots.com" cta="Redeem" />
            </div>
          )}
        </div>
        {/* Pollen Dots of Care — consolidated across all this customer's
            tours. Surfaces only when at least one is earned; otherwise
            stays hidden so a guest never sees a zero. */}
        {(data.pollen_dots_of_care || 0) > 0 && (
          <div className="mt-2.5">
            <PollenDotsOfCareStat
              count={data.pollen_dots_of_care}
              breakdown={data.pollen_dots_of_care_breakdown}
            />
          </div>
        )}

        <ReshareNudge />

        <ShareKit token={token} ogShareUrl={data.og_share_url} firstName={firstName} />

        <PollenDotsContactFooter />
      </div>
    </div>
  );
};

/* ---------- Share Kit ----------
 * Ready-to-post branded images of this passport in every social ratio,
 * generated server-side (same brand card used for link previews). Plus a
 * "smart link" — pasting it anywhere auto-unfurls title + map image. */
const SHARE_PRESETS = [
  { key: 'square',    label: 'Post 1:1',      hint: 'Instagram / FB post' },
  { key: 'portrait',  label: 'Feed 4:5',      hint: 'Instagram feed' },
  { key: 'story',     label: 'Story 9:16',    hint: 'Stories / Reels / Status' },
  { key: 'landscape', label: 'Wide 16:9',     hint: 'YouTube / banner / X' },
];

const ShareKit = ({ token, ogShareUrl, firstName }) => {
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);

  const download = async (preset) => {
    setBusy(preset);
    try {
      const res = await fetch(`${API}/public/journey/${token}/card/${preset}.png`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(firstName || 'pollen').toLowerCase()}-dots-of-connection-${preset}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      /* silent — server hiccup */
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(ogShareUrl || window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="mt-8" data-testid="share-kit">
      <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-center" style={{ color: '#B16E32' }}>
        Share kit
      </p>
      <p className="text-sm text-center mt-1 mb-3" style={{ color: '#6b5566' }}>
        Ready-to-post images of this passport, in every format.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {SHARE_PRESETS.map(p => (
          <button key={p.key} type="button" onClick={() => download(p.key)} disabled={busy !== null}
            data-testid={`share-kit-${p.key}`}
            className="rounded-xl px-3 py-3 text-left bg-white transition hover:shadow-md disabled:opacity-60"
            style={{ border: '1px solid #EBD9CD' }}>
            <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: '#581C2B' }}>
              {busy === p.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" style={{ color: '#B16E32' }} />}
              {p.label}
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: '#787878' }}>{p.hint}</div>
          </button>
        ))}
      </div>
      {ogShareUrl && (
        <button type="button" onClick={copyLink} data-testid="share-kit-copy-link"
          className="mt-2.5 w-full rounded-xl px-4 py-3 flex items-center justify-between gap-3 bg-white transition hover:shadow-md text-left"
          style={{ border: '1px solid #EBD9CD' }}>
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: '#581C2B' }}>
              <Link2 className="w-3.5 h-3.5" style={{ color: '#B16E32' }} /> {copied ? 'Copied!' : 'Copy smart link'}
            </div>
            <div className="text-[10px] mt-0.5 truncate" style={{ color: '#787878' }}>
              Paste anywhere — title, description &amp; map image appear automatically.
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wider font-semibold shrink-0" style={{ color: '#B16E32' }}>
            {copied ? '✓' : 'Copy'}
          </span>
        </button>
      )}
    </div>
  );
};

/* ---------- Small atoms ---------- */
const BrandBar = () => (
  <div className="flex items-center justify-center" data-testid="share-brand-bar">
    {/* Logo path uses /og/ rather than /pollen-logo.png at root because the
        ArtWorksIT Nginx proxy on pollendots.com only forwards specific path
        prefixes (/connection, /static, /api, /og, /manifest.json,
        /favicon.ico). A bare PNG at root would fall through to the marketing
        site and 404. The Emergent CRA build copies everything in public/
        verbatim, so /og/pollen-logo.png is served correctly on both
        intelligence.thecaravannetwork.com and pollendots.com. */}
    <img src="/og/pollen-logo.png" alt="Pollen Dots" className="h-7 sm:h-8 w-auto" data-testid="share-brand-logo" />
  </div>
);

// ---- ReshareNudge ----
// Soft CTA shown on every public share page. Opens the device native share
// sheet (WhatsApp / iMessage / Mail / etc. all in one tap on mobile) with a
// pre-filled intro pointing to https://www.pollendots.com.
//
// Copy note: this card is shown to the CUSTOMER opening their own share
// page, so the headline must work for them too — phrased as a soft
// invitation to spread Pollen Dots, not a third-party referral pitch.
//
// Implementation note on the WhatsApp duplicate-URL bug: passing both
// `text` and `url` to navigator.share makes some Android share targets
// (WhatsApp especially) concatenate them and render the URL twice. We
// inline the URL inside `text` and OMIT the `url` field, which produces
// a clean single-URL message everywhere.
const ReshareNudge = () => {
  const text =
    `Discover Pollen Dots — Journeys curated with care. 🌸\n` +
    `https://www.pollendots.com`;
  const onClick = () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        navigator.share({
          title: 'Pollen Dots',
          text,
        }).catch(() => { /* user cancelled — silent */ });
        return;
      }
    } catch { /* fall through */ }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };
  return (
    <div className="mt-6" data-testid="reshare-nudge">
      <button
        type="button"
        onClick={onClick}
        className="group w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl border border-rose-200/70 bg-gradient-to-r from-rose-50/80 via-amber-50/60 to-teal-50/70 hover:shadow-md hover:border-rose-300 transition-all text-left"
        aria-label="Pass on Pollen Dots"
      >
        <div className="flex items-start gap-3 min-w-0">
          <span className="text-2xl leading-none shrink-0" aria-hidden>🌸</span>
          <div className="min-w-0">
            <div className="text-sm sm:text-[15px] font-medium text-foreground/90 leading-snug">
              Pass it on
            </div>
            <div className="text-[11px] sm:text-xs text-foreground/60 mt-0.5">
              Pollen Dots is built on word of mouth. Share with someone who'd appreciate the care.
            </div>
          </div>
        </div>
        <span className="text-xs sm:text-sm font-semibold text-rose-700 whitespace-nowrap shrink-0 flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
          Share <span aria-hidden>→</span>
        </span>
      </button>
    </div>
  );
};

const TINTS = {
  teal:   { bg: 'bg-teal-50/80',    border: 'border-teal-200/70',    text: 'text-teal-700',    icon: 'text-teal-500' },
  sky:    { bg: 'bg-sky-50/80',     border: 'border-sky-200/70',     text: 'text-sky-700',     icon: 'text-sky-500' },
  rose:   { bg: 'bg-rose-50/80',    border: 'border-rose-200/70',    text: 'text-rose-700',    icon: 'text-rose-500' },
  orange: { bg: 'bg-orange-50/80',  border: 'border-orange-200/70',  text: 'text-orange-700',  icon: 'text-orange-500' },
  violet: { bg: 'bg-violet-50/80',  border: 'border-violet-200/70',  text: 'text-violet-700',  icon: 'text-violet-500' },
  pink:   { bg: 'bg-pink-50/80',    border: 'border-pink-200/70',    text: 'text-pink-700',    icon: 'text-pink-500' },
  amber:  { bg: 'bg-amber-50',      border: 'border-amber-300/80',   text: 'text-amber-800',   icon: 'text-amber-500' },
  fuchsia:{ bg: 'bg-fuchsia-50/80', border: 'border-fuchsia-200/70', text: 'text-fuchsia-700', icon: 'text-fuchsia-500' },
};

// Pollen Dots of Care counter — same StatCard chrome as the other counters
// but tap/hover reveals a small popover that reads back what produced the
// number. Turns the tally into a quiet little story for the guest.
const buildCareStory = (b) => {
  if (!b) return null;
  const parts = [];
  const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;
  if (b.stops > 0)      parts.push(plural(b.stops,      'stop of care',      'stops of care'));
  if (b.hotels > 0)     parts.push(plural(b.hotels,     'hotel of care',     'hotels of care'));
  if (b.activities > 0) parts.push(plural(b.activities, 'activity of care',  'activities of care'));
  if (b.guide_days > 0) parts.push(plural(b.guide_days, 'day walked with a local guide', 'days walked with local guides'));
  if (parts.length === 0) return null;
  // Join: "a, b, and c"
  let body;
  if (parts.length === 1) body = parts[0];
  else if (parts.length === 2) body = `${parts[0]} and ${parts[1]}`;
  else body = `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  const tourLine = b.tours > 0
    ? `Earned across ${b.tours === 1 ? '1 tour' : `${b.tours} tours`}: `
    : '';
  return `${tourLine}${body}.`;
};

const PollenDotsOfCareStat = ({ count, breakdown }) => {
  const story = buildCareStory(breakdown);
  // One-shot amber shimmer to draw the eye toward the TAP affordance on
  // first paint. Only fires when there's a story to reveal — silent
  // counters (1 stop, no breakdown) wouldn't reward the tap and we don't
  // want to nag. Auto-removes after the keyframe finishes so subsequent
  // renders sit quietly without re-glowing.
  const [shimmer, setShimmer] = React.useState(!!story);
  React.useEffect(() => {
    if (!story) return undefined;
    const id = setTimeout(() => setShimmer(false), 2200);
    return () => clearTimeout(id);
  }, [story]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded-xl"
          data-testid="counter-pollen-dots-of-care"
        >
          <div className={`relative bg-amber-50 border border-amber-300/80 backdrop-blur rounded-xl px-3 py-2.5 flex items-center gap-2.5 transition min-w-0 hover:shadow-sm hover:bg-amber-50/90 cursor-pointer ${shimmer ? 'pollen-care-shimmer' : ''}`}>
            <div className="shrink-0 w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center text-amber-500">
              <HandHeart className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-lg font-bold tabular-nums leading-none text-amber-800">{count}</div>
              <div className="text-[10px] text-foreground/60 mt-1 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">Pollen Dots of Care</div>
            </div>
            {story && (
              <span className="shrink-0 text-[10px] uppercase tracking-wider font-semibold text-amber-700/80">
                Tap
              </span>
            )}
          </div>
        </button>
      </PopoverTrigger>
      {story && (
        <PopoverContent
          align="center"
          sideOffset={8}
          className="max-w-[280px] sm:max-w-[320px] bg-gradient-to-br from-amber-50 via-white to-rose-50 border-amber-200"
          data-testid="counter-pollen-dots-of-care-popover"
        >
          <div className="flex items-start gap-2">
            <div className="shrink-0 w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mt-0.5">
              <HandHeart className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700/80">
                Pollen Dots of Care
              </p>
              <p className="text-sm leading-relaxed text-foreground/80 mt-1">
                {story}
              </p>
              <p className="text-[10px] text-muted-foreground/80 italic mt-2 leading-relaxed">
                Each dot is a small act of care woven into your journey.
              </p>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

export const StatCard = ({ Icon, tint = 'teal', label, value, testid, href, cta }) => {
  const t = TINTS[tint] || TINTS.teal;
  const base = `relative ${t.bg} ${t.border} backdrop-blur border rounded-xl px-3 py-2.5 flex items-center gap-2.5 transition min-w-0`;
  const inner = (
    <>
      <div className={`shrink-0 w-8 h-8 rounded-lg bg-white/70 flex items-center justify-center ${t.icon}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-lg font-bold tabular-nums leading-none ${t.text}`}>{value}</div>
        <div className="text-[10px] text-foreground/60 mt-1 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">{label}</div>
        {cta && (
          <div className={`text-[10px] font-semibold uppercase tracking-wider ${t.text} mt-1.5 inline-flex items-center gap-0.5`}>
            {cta} <span aria-hidden>&rarr;</span>
          </div>
        )}
      </div>
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className={`${base} group hover:shadow-sm hover:border-primary/60 hover:bg-white cursor-pointer`}>{inner}</a>
    );
  }
  return <div data-testid={testid} className={base}>{inner}</div>;
};

export default PublicJourneyShare;
